/**
 * TabArtefacts — view, edit, and create starting artefacts in the template.
 * Shows all artefacts grouped by country/role with expandable content and inline editing.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

const DEFAULT_SIM_RUN_ID = '00000000-0000-0000-0000-000000000001'

interface Artefact {
  id: string
  sim_run_id: string
  role_id: string
  visible_to: string[] | null
  title: string
  classification: string
  artefact_type: string
  subtitle: string | null
  from_entity: string | null
  date_label: string | null
  content_html: string | null
  round_delivered: number
  is_read: boolean
}

interface RoleInfo {
  id: string
  character_name: string
  country_code: string
  title: string
}

const ARTEFACT_TYPES = [
  { value: 'classified_report', label: 'Classified Report' },
  { value: 'telegram', label: 'Diplomatic Cable' },
  { value: 'intelligence_report', label: 'Intelligence Report' },
  { value: 'memo', label: 'Policy Memo' },
  { value: 'briefing', label: 'Briefing Document' },
] as const

const CLASSIFICATIONS = [
  'CONFIDENTIAL', 'SECRET', 'TOP SECRET', 'FLASH', 'EYES ONLY',
  'BY ORDER OF THE SUPREME LEADER', 'PRESIDENTIAL', 'WAR CABINET',
] as const

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function classColor(c: string): string {
  const cl = c.toUpperCase()
  if (cl.includes('TOP SECRET') || cl.includes('FLASH') || cl.includes('SUPREME')) return 'bg-danger/15 text-danger'
  if (cl.includes('SECRET') || cl.includes('EYES ONLY') || cl.includes('PRESIDENTIAL') || cl.includes('WAR CABINET')) return 'bg-warning/15 text-warning'
  return 'bg-text-secondary/10 text-text-secondary'
}

function typeLabel(t: string): string {
  return ARTEFACT_TYPES.find(at => at.value === t)?.label ?? cap(t.replace(/_/g, ' '))
}

export function TabArtefacts({ templateId }: { templateId: string }) {
  const [artefacts, setArtefacts] = useState<Artefact[]>([])
  const [roles, setRoles] = useState<RoleInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const loadData = useCallback(async () => {
    const [artRes, roleRes] = await Promise.all([
      supabase.from('artefacts').select('*').eq('sim_run_id', DEFAULT_SIM_RUN_ID).order('role_id'),
      supabase.from('roles').select('id,character_name,country_code,title').eq('sim_run_id', DEFAULT_SIM_RUN_ID).order('country_code'),
    ])
    setArtefacts((artRes.data ?? []) as Artefact[])
    setRoles((roleRes.data ?? []) as RoleInfo[])
    setLoading(false)
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const roleMap = useMemo(() => {
    const m: Record<string, RoleInfo> = {}
    roles.forEach(r => { m[r.id] = r })
    return m
  }, [roles])

  const byCountry = useMemo(() => {
    const groups: Record<string, { country: string; artefacts: (Artefact & { role?: RoleInfo })[] }> = {}
    artefacts.forEach(a => {
      const role = roleMap[a.role_id]
      const cc = role?.country_code ?? 'unknown'
      if (!groups[cc]) groups[cc] = { country: cc, artefacts: [] }
      groups[cc].artefacts.push({ ...a, role })
    })
    return Object.values(groups).sort((a, b) => a.country.localeCompare(b.country))
  }, [artefacts, roleMap])

  const handleSave = async (id: string, updates: Partial<Artefact>) => {
    setSaving(true)
    try {
      await supabase.from('artefacts').update(updates).eq('id', id)
      setArtefacts(prev => prev.map(a => a.id === id ? { ...a, ...updates } : a))
      setEditingId(null)
    } catch (e) { console.error('Save failed:', e) }
    finally { setSaving(false) }
  }

  const handleCreate = async () => {
    const newId = `art_new_${Date.now()}`
    const row = {
      id: newId,
      sim_run_id: DEFAULT_SIM_RUN_ID,
      role_id: roles[0]?.id ?? '',
      title: 'New Artefact',
      classification: 'CONFIDENTIAL',
      artefact_type: 'memo',
      subtitle: null,
      from_entity: null,
      date_label: null,
      content_html: '<h3>Content</h3><p>Enter artefact content here.</p>',
      round_delivered: 0,
      is_read: false,
    }
    try {
      await supabase.from('artefacts').insert(row)
      setArtefacts(prev => [...prev, row as Artefact])
      setEditingId(newId)
      setExpandedId(newId)
    } catch (e) { console.error('Create failed:', e) }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this artefact? This cannot be undone.')) return
    try {
      await supabase.from('artefacts').delete().eq('id', id)
      setArtefacts(prev => prev.filter(a => a.id !== id))
      if (editingId === id) setEditingId(null)
      if (expandedId === id) setExpandedId(null)
    } catch (e) { console.error('Delete failed:', e) }
  }

  if (loading) {
    return <p className="font-body text-body-sm text-text-secondary py-8 text-center">Loading artefacts...</p>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-h3 text-text-primary">Starting Artefacts</h3>
        <div className="flex items-center gap-3">
          <span className="font-data text-caption text-text-secondary">{artefacts.length} artefact{artefacts.length !== 1 ? 's' : ''}</span>
          <button onClick={handleCreate}
            className="font-body text-body-sm text-action hover:underline">+ New Artefact</button>
        </div>
      </div>

      {artefacts.length === 0 ? (
        <p className="font-body text-body-sm text-text-secondary bg-base border border-border rounded-lg p-6 text-center">
          No starting artefacts. Click "+ New Artefact" to create one.
        </p>
      ) : (
        <div className="space-y-4">
          {byCountry.map(group => (
            <div key={group.country}>
              <h4 className="font-heading text-body-sm text-text-primary uppercase tracking-wider mb-2">
                {cap(group.country)}
              </h4>
              <div className="space-y-1.5">
                {group.artefacts.map(a => {
                  const isExpanded = expandedId === a.id
                  const isEditing = editingId === a.id
                  return (
                    <div key={a.id} className="border border-border rounded-lg overflow-hidden">
                      {/* Header row */}
                      <div className="flex items-center gap-3 px-4 py-2.5">
                        <button onClick={() => { setExpandedId(isExpanded ? null : a.id); if (isEditing && !isExpanded) setEditingId(null) }}
                          className="flex items-center gap-3 flex-1 min-w-0 text-left hover:bg-base/50 transition-colors -mx-1 px-1 rounded">
                          <span className={`font-data text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider shrink-0 ${classColor(a.classification)}`}>
                            {a.classification}
                          </span>
                          <span className="font-body text-body-sm text-text-primary font-medium flex-1 truncate">{a.title}</span>
                          <span className="font-body text-caption text-text-secondary shrink-0">
                            {a.role?.character_name ?? a.role_id} · {a.role?.title ?? ''}
                            {a.visible_to && a.visible_to.length > 0 && (
                              <span className="text-action/60 ml-1">
                                + {a.visible_to.map(rid => roleMap[rid]?.character_name ?? rid).join(', ')}
                              </span>
                            )}
                          </span>
                          <span className="font-body text-caption text-text-secondary/50 shrink-0">{typeLabel(a.artefact_type)}</span>
                          <span className="font-data text-caption text-text-secondary/40 shrink-0">R{a.round_delivered}</span>
                        </button>
                        <button onClick={() => { setExpandedId(a.id); setEditingId(isEditing ? null : a.id) }}
                          className="font-body text-caption text-action hover:underline shrink-0">
                          {isEditing ? 'Cancel' : 'Edit'}
                        </button>
                        <button onClick={() => handleDelete(a.id)}
                          className="font-body text-caption text-danger/60 hover:text-danger shrink-0">Del</button>
                        <span className="text-text-secondary/40 shrink-0">{isExpanded ? '▾' : '▸'}</span>
                      </div>

                      {/* Expanded: view or edit */}
                      {isExpanded && !isEditing && (
                        <div className="border-t border-border bg-base/30 px-5 py-4">
                          {a.from_entity && <p className="font-body text-caption text-text-secondary mb-2">From: {a.from_entity}</p>}
                          {a.content_html ? (
                            <div className="font-body text-body-sm text-text-primary prose prose-sm max-w-none
                              [&_h3]:font-heading [&_h3]:text-body-sm [&_h3]:text-text-primary [&_h3]:mt-3 [&_h3]:mb-1
                              [&_p]:mb-2 [&_ul]:mb-2 [&_li]:mb-0.5
                              [&_.highlight]:bg-warning/10 [&_.highlight]:border [&_.highlight]:border-warning/20 [&_.highlight]:rounded [&_.highlight]:p-2 [&_.highlight]:my-2"
                              dangerouslySetInnerHTML={{ __html: a.content_html }} />
                          ) : (
                            <p className="font-body text-caption text-text-secondary italic">No content</p>
                          )}
                        </div>
                      )}

                      {isExpanded && isEditing && (
                        <ArtefactEditor artefact={a} roles={roles} saving={saving}
                          onSave={(updates) => handleSave(a.id, updates)}
                          onCancel={() => setEditingId(null)} />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ========================================================================== */
/*  Artefact Editor                                                            */
/* ========================================================================== */

function ArtefactEditor({ artefact, roles, saving, onSave, onCancel }: {
  artefact: Artefact & { role?: RoleInfo }
  roles: RoleInfo[]
  saving: boolean
  onSave: (updates: Partial<Artefact>) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(artefact.title)
  const [roleId, setRoleId] = useState(artefact.role_id)
  const [classification, setClassification] = useState(artefact.classification)
  const [artefactType, setArtefactType] = useState(artefact.artefact_type)
  const [fromEntity, setFromEntity] = useState(artefact.from_entity ?? '')
  const [roundDelivered, setRoundDelivered] = useState(artefact.round_delivered)
  const [visibleTo, setVisibleTo] = useState<string[]>(artefact.visible_to ?? [])
  const [contentHtml, setContentHtml] = useState(artefact.content_html ?? '')

  // Group roles by country
  const sortedRoles = [...roles].sort((a, b) => {
    const cc = a.country_code.localeCompare(b.country_code)
    return cc !== 0 ? cc : a.character_name.localeCompare(b.character_name)
  })

  return (
    <div className="border-t border-action/30 bg-action/5 px-5 py-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block font-body text-caption text-text-secondary mb-1">Title</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)}
            className="w-full bg-base border border-border rounded px-3 py-1.5 font-body text-body-sm text-text-primary focus:outline-none focus:border-action" />
        </div>
        <div>
          <label className="block font-body text-caption text-text-secondary mb-1">Recipient Role</label>
          <select value={roleId} onChange={e => setRoleId(e.target.value)}
            className="w-full bg-base border border-border rounded px-3 py-1.5 font-body text-body-sm text-text-primary focus:outline-none focus:border-action">
            {sortedRoles.map(r => (
              <option key={r.id} value={r.id}>{r.character_name} — {cap(r.country_code)}, {r.title}</option>
            ))}
          </select>
        </div>
      </div>
      {/* Also visible to */}
      <div>
        <label className="block font-body text-caption text-text-secondary mb-1">Also visible to (additional roles)</label>
        <div className="flex flex-wrap gap-1.5">
          {sortedRoles.filter(r => r.id !== roleId).map(r => {
            const sel = visibleTo.includes(r.id)
            return (
              <button key={r.id} type="button"
                onClick={() => setVisibleTo(prev => sel ? prev.filter(x => x !== r.id) : [...prev, r.id])}
                className={`font-body text-caption px-2 py-0.5 rounded border transition-colors ${
                  sel ? 'bg-action/15 text-action border-action/30' : 'bg-base border-border text-text-secondary hover:border-action/20'
                }`}>
                {r.character_name} ({cap(r.country_code)})
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div>
          <label className="block font-body text-caption text-text-secondary mb-1">Classification</label>
          <select value={classification} onChange={e => setClassification(e.target.value)}
            className="w-full bg-base border border-border rounded px-3 py-1.5 font-body text-body-sm text-text-primary focus:outline-none focus:border-action">
            {CLASSIFICATIONS.map(c => <option key={c} value={c}>{c}</option>)}
            {!CLASSIFICATIONS.includes(classification as typeof CLASSIFICATIONS[number]) && (
              <option value={classification}>{classification}</option>
            )}
          </select>
        </div>
        <div>
          <label className="block font-body text-caption text-text-secondary mb-1">Type</label>
          <select value={artefactType} onChange={e => setArtefactType(e.target.value)}
            className="w-full bg-base border border-border rounded px-3 py-1.5 font-body text-body-sm text-text-primary focus:outline-none focus:border-action">
            {ARTEFACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block font-body text-caption text-text-secondary mb-1">From</label>
          <input type="text" value={fromEntity} onChange={e => setFromEntity(e.target.value)}
            placeholder="e.g. Office of the President"
            className="w-full bg-base border border-border rounded px-3 py-1.5 font-body text-body-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-action" />
        </div>
        <div>
          <label className="block font-body text-caption text-text-secondary mb-1">Round</label>
          <input type="number" min={0} max={8} value={roundDelivered}
            onChange={e => setRoundDelivered(Math.max(0, parseInt(e.target.value) || 0))}
            className="w-full bg-base border border-border rounded px-3 py-1.5 font-data text-data text-text-primary focus:outline-none focus:border-action" />
        </div>
      </div>
      <div>
        <label className="block font-body text-caption text-text-secondary mb-1">Content (HTML)</label>
        <textarea value={contentHtml} onChange={e => setContentHtml(e.target.value)}
          rows={12}
          className="w-full bg-base border border-border rounded px-3 py-2 font-data text-caption text-text-primary focus:outline-none focus:border-action resize-y leading-relaxed" />
        <p className="font-body text-caption text-text-secondary/50 mt-1">
          Supports: &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;/&lt;li&gt;, &lt;div class="highlight"&gt;
        </p>
      </div>
      <div className="flex gap-2">
        <button onClick={() => onSave({
          title, role_id: roleId, classification, artefact_type: artefactType,
          from_entity: fromEntity || null, round_delivered: roundDelivered,
          content_html: contentHtml || null, visible_to: visibleTo,
        } as Partial<Artefact>)} disabled={saving}
          className="bg-action text-white font-body text-body-sm font-medium py-1.5 px-5 rounded hover:opacity-90 disabled:opacity-50 transition-opacity">
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onCancel}
          className="font-body text-body-sm text-text-secondary hover:text-text-primary px-4">Cancel</button>
      </div>
    </div>
  )
}
